<template>
  <div v-if="currentTeam" class="settings-main">
    <ExpandableSettingsItem
      v-if="currentUser && currentUser.id"
      :title="$t('message.NOTIFICATIONS')"
      class="settings-main__notifications"
    >
      <div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="$t('message.NOTIFICATIONS_SETTINGS_INFO')" />
        <div class="settings-main__notifications__content-wrapper">
          <div class="v-centerer mb5em">
            <div class="mr5em legend">
              {{ `${$t('message.NOTIFICATIONS_SEE')}${$t('message.TWO_DOTS')}` }}
            </div>
            <Switcher v-model="notificationSettingsView" :titles="notificationSettingsViews" />
          </div>
          <table class="settings-main__notifications__table">
            <thead>
              <tr>
                <th>{{ $t('message.NOTIFICATION_TYPE') }}</th>
                <th>{{ $t('message.NOTIFICATION') }}</th>
                <th>{{ $t('message.NOTIFICATION_EMAIL') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="type in filteredNotificationTypes"
                :key="type.id"
                class="settings-main__notifications__item"
              >
                <td class="settings-main__notifications__item__name">
                  <div>{{ $t(`message.NOTIF_TYPE_${type.codeName}`) }}</div>
                  <div
                    v-if="type.notifType.durationProperty"
                    class="v-centerer mt5em settings-main__notifications__item__duration-edition"
                  >
                    <DurationPicker
                      v-model="type[type.notifType.durationProperty]"
                      show-days
                      min
                      @input="EDIT_NOTIFICATION_SETTINGS({ types: [type] })"
                    />
                    <span>{{ `&nbsp;${$t('message.DURATION_BEFORE')}` }}</span>
                  </div>
                </td>
                <td class="settings-main__notifications__item__checkbox-wrapper">
                  <div class="h-v-centerer">
                    <IntCheckbox v-model="type.enabled" @input="EDIT_NOTIFICATION_SETTINGS({ types: [type] })" />
                  </div>
                </td>
                <td class="settings-main__notifications__item__checkbox-wrapper">
                  <div class="h-v-centerer">
                    <IntCheckbox v-model="type.emailEnabled" @input="EDIT_NOTIFICATION_SETTINGS({ types: [type] })" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.IA_HINTS')">
      <div>
        <IntCheckbox
          :value="currentTeam.hideHints"
          :title="$t('message.HIDE_HINTS')"
          @input="save('hideHints', $event)"
        />
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.LANGUAGE')">
      <div class="settings-main__langs">
        <div
          v-for="lang in languages"
          :key="lang.value"
          class="settings-main__langs__item v-centerer"
          :class="{ 'settings-main__langs__item--selected': lang.value == currentLang }"
          @click="saveLang(lang)"
        >
          <img :src="'/images/flags/' + lang.flag" alt="">
          <span class="ml10 notranslate">{{ lang.name }}</span>
        </div>
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.SCREEN_SAVER')">
      <div>
        <div class="generic-background">
          <IntCheckbox
            :value="currentTeam.screenSaverOn"
            :title="$t('message.ENABLE_SCREEN_SAVER')"
            @input="save('screenSaverOn', $event)"
          />
          <IntCheckbox
            :value="currentTeam.screenSaverKeepPage"
            class="block"
            :title="$t('message.KEEP_SCREEN_SAVER_PAGE')"
            @input="save('screenSaverKeepPage', $event)"
          />
          <div class="screen-saver-timeout v-centerer">
            <span class="legend">
              {{ `${$t('message.SCREEN_SAVER_TIMEOUT')}${$t('message.TWO_DOTS')}` }}
            </span>
            <VueTimepicker
              v-model="ssFormattedTimeout"
              class="ml5"
              format="HH:mm:ss"
              :second-interval="10"
            />
          </div>
          <div class="legend legend--bottom-margin">
            {{ $t('message.PAGES') }}
          </div>
          <SortablePagePickers v-model="ssPages" screen-saver />
        </div>
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.DEFAULT_HOME_PAGE')">
      <div>
        <PagePicker
          :value="currentTeam.defaultHomePage || { pageId: SPage.DASHBOARD.id }"
          see-page
          @input="save('defaultHomePage', $event)"
        />
      </div>
    </ExpandableSettingsItem>
    <NavigationSettings />
    <ExpandableSettingsItem :title="$t('message.DISP_ALL_ANNOUNCEMENTS')">
      <div>
        <div class="field-hint">
          {{ $t('message.DISP_ANNOUNCEMENTS_HINT') }}
        </div>
        <div class="field-hint">
          {{ `${$t('message.CURRENT_CLOSED_ANNOUNCEMENTS')}${$t('message.TWO_DOTS')}${nHiddenAnnouncements}` }}
        </div>
        <button
          class="generic-border generic-border--text-btn generic-border--blue settings-main__announcements"
          @click="$store.commit(SHOW_ALL_ANNOUNCEMENTS)"
        >
          {{ $t('message.DISPLAY') }}
        </button>
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('release.RELEASE_NOTES')">
      <div class="release-notes-settings">
        <div class="field-hint">
          {{ $t('release.RELEASE_NOTES_SETTINGS_HINT') }}
        </div>
        <Checkbox
          :value="hideReleaseEssential"
          :title="$t('release.DONT_SHOW_ESSENTIAL_AGAIN')"
          :disabled="hideAllReleaseEssential"
          @input="SET_HIDE_RELEASE_ESSENTIAL"
        />
        <div class="field-hint">
          {{ $t('release.HIDE_CURRENT_ESSENTIAL_HINT') }}
        </div>
        <Checkbox
          :value="hideAllReleaseEssential"
          :title="$t('release.DONT_SHOW_ANY_ESSENTIAL_AGAIN')"
          @input="SET_HIDE_ALL_RELEASE_ESSENTIAL"
        />
        <div class="field-hint">
          {{ $t('release.HIDE_ALL_ESSENTIAL_HINT') }}
        </div>
        <button
          class="generic-border generic-border--text-btn generic-border--blue"
          @click="displayReleaseNotes = true"
        >
          {{ $t('release.SHOW_ALL_NOTES') }}
        </button>

        <ReleaseNotes :displayed="displayReleaseNotes" @close="displayReleaseNotes = false" />
      </div>
    </ExpandableSettingsItem>
  </div>
</template>

<script>
// Import components
import VueTimepicker from 'vue2-timepicker';
import { cloneDeep, isEqual } from 'lodash';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import Checkbox from '@/components/checkbox.vue';
import DurationPicker from '@/components/duration-picker.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import NavigationSettings from '@/components/navigation-settings.vue';
import PagePicker from '@/components/page-picker.vue';
import ReleaseNotes from '@/components/release-notes.vue';
import SortablePagePickers from '@/components/sortable-page-pickers.vue';
import Switcher from '@/components/switcher.vue';
// Import Vuex utils
import {
  FETCH_SCREEN_SAVER,
  SET_TEAM_PARAM,
  FETCH_NOTIFICATION_SETTINGS,
  EDIT_NOTIFICATION_SETTINGS,
} from '@/store/action-types';
import {
  GET_SCREEN_SAVER,
  SHOW_ALL_ANNOUNCEMENTS,
  SET_HIDE_RELEASE_ESSENTIAL,
  SET_HIDE_ALL_RELEASE_ESSENTIAL,
  GET_NOTIFICATION_SETTINGS,
} from '@/store/mutation-types';
import { SETUP_LANGUAGE } from '@/store/modules/language/action-types';
// Utils
import SPage from '@/utils/classes/SPage';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { NotificationType } from '@/utils/notification-utils';

export default {

  name: 'SettingsMain',

  components: {
    Checkbox,
    DurationPicker,
    ExpandableSettingsItem,
    IntCheckbox,
    NavigationSettings,
    PagePicker,
    ReleaseNotes,
    SortablePagePickers,
    Switcher,
    VueTimepicker,
  },

  data() {
    return {
      SPage,
      SHOW_ALL_ANNOUNCEMENTS,
      languages: [
        { name: 'English', flag: 'uk.png', value: 'en' },
        { name: 'Français', flag: 'france.png', value: 'fr' },
        { name: 'Español', flag: 'spain.png', value: 'es' },
      ],
      currentLang: this.$store.state.language.language,
      ssPages: [],
      // In release notes section
      displayReleaseNotes: false,
      notificationSettingsViews: [this.$t('message.TEAM'), this.$t('message.USER')],
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.SETTINGS_MAIN'),
    };
  },

  computed: {
    /** Screen saver timeout readable by VueTimepicker */
    ssFormattedTimeout: {
      get() {
        const duration = this.$moment.duration(Math.floor(this.currentTeam.screenSaverTimeout), 's');
        if (duration.asHours() >= 24) {
          return { HH: '23', mm: '59', ss: '59' };
        }
        return {
          HH: (duration.hours() < 10 ? '0' : '') + String(duration.hours()),
          mm: (duration.minutes() < 10 ? '0' : '') + String(duration.minutes()),
          ss: (duration.seconds() < 10 ? '0' : '') + String(duration.seconds()),
        };
      },
      set(val) {
        let secs = Number(val.HH) * 3600 + Number(val.mm) * 60 + Number(val.ss);
        secs = Math.max(secs, 10);
        if (secs !== this.currentTeam.screenSaverTimeout) {
          this.save('screenSaverTimeout', secs < 10 ? 10 : secs);
        }
      },
    },
    /** Boolean: Filtered view of the notification settings */
    notificationSettingsView: {
      get() {
        return Number(this.$route.query.mainSettNotifSettingsView) === 1 ? 1 : 0;
      },
      set(view) {
        const query = Object.assign({}, this.$route.query);
        query.mainSettNotifSettingsView = view;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Array: user-related or team-related notification types */
    filteredNotificationTypes() {
      // NOTE: Type 13 is PROBLEM_UPDATE which has been implemented for Cartier version, but not in Shizen yet,
      // As it relies on the problem subscription system
      const types = this.notificationsSettings.filter(nt => nt.isUserNotification === this.notificationSettingsView
        && nt.id !== NotificationType.PROBLEM_UPDATE.id);
      return cloneDeep(types);
    },
    ...mapState({
      screenSaverPagesLoading: state => state[GET_SCREEN_SAVER.loadingKey],
      language: state => state.language.language,
      hideReleaseEssential: state => state.hideReleaseEssential,
      hideAllReleaseEssential: state => state.hideAllReleaseEssential,
      notificationsSettings: state => state[GET_NOTIFICATION_SETTINGS.stateKey] || [],
      notificationsSettingsLoading: state => state[GET_NOTIFICATION_SETTINGS.loadingKey],
    }),
    ...mapGetters([
      'nHiddenAnnouncements',
    ]),
  },

  created() {
    if (!this.screenSaverPagesLoading) {
      this.$store.dispatch(FETCH_SCREEN_SAVER).then(() => {
        this.ssPages = this.$store.state[GET_SCREEN_SAVER.stateKey] || [];
      });
    }

    if (!this.notificationsSettingsLoading && this.currentUser && this.currentUser.id) {
      this.$store.dispatch(FETCH_NOTIFICATION_SETTINGS);
    }
  },

  methods: {
    save(param, val) {
      this.$store.dispatch(SET_TEAM_PARAM, { [param]: val });
    },
    saveLang(lang) {
      globalEventDispatcher.dispatch(`${ShizenGlobalEvent.SET_LANGUAGE}`, lang.value);
      this.$store.dispatch(`language/${SETUP_LANGUAGE}`, lang.value)
        .then(() => {
          // Force reload to update current page translations
          const currentPath = this.$route.fullPath;
          this.$router.replace('/dashboard');
          this.$nextTick(() => {
            this.$router.replace(currentPath);
          });
        });
    },
    ...mapMutations({
      SET_HIDE_RELEASE_ESSENTIAL,
      SET_HIDE_ALL_RELEASE_ESSENTIAL,
    }),
    ...mapActions({
      EDIT_NOTIFICATION_SETTINGS,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.release-notes-settings {
  .field-hint {
    margin-bottom: 1.2em;
  }
}

.settings-main {

  &__notifications {

    &__content-wrapper {
      padding: 1em 0.5em;
      margin-top: 1em;
    }

    &__table {
      margin-top: 1.5rem;
      border-collapse: collapse;

      th, td {
        padding: 0.2em 0.5em;
      }

      th:first-child {
        text-align: left;
      }
    }

    &__item {

      &:not(:last-child) {
        border-bottom: 1px solid $color-gray-background;
      }

      & &__name {
        padding: 1em 0.5em;
      }

      &__checkbox-wrapper {

        .checkbox {
          padding: 1em;

          & .tick {
            margin-right: 0;
          }

          &:hover {
            background: $color-hoverbtn-light-blue;
          }
        }
      }

      &__duration-edition {
        display: inline-flex;
        padding-right: 0.5em;
        border: 1px solid rgba(0, 0, 0, 0.15);
      }
    }
  }

  &__langs {
    &__item {
      padding: 0.5rem;
      border-radius: $high-border-radius;
      margin-bottom: 0.5rem;
      cursor: pointer;

      img {
        width: 2rem;
      }

      &--selected {
        background: $color-gray-background-hover;
      }

      &:hover {
        background: $main-hover-color;
      }
    }
  }

  &__announcements {
    margin-top: 1rem;
  }
}
</style>
